/**
 * School terms: which academic year a term belongs to, and how a term's months
 * are written. One copy, shared by the beneficiary form (pages/Students.jsx)
 * and the read-only panel (lib/recordFields.js). (ZEN-783)
 */
const SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const parseYm = (ym) => {
  const m = typeof ym === "string" ? ym.match(/^(\d{4})-(\d{2})$/) : null;
  if (!m) return null;
  const y = +m[1], mo = +m[2];
  return mo >= 1 && mo <= 12 ? { y, m: mo } : null;
};

/** Every "YYYY-MM" from `from` to `to`, inclusive (capped at ten years). */
export function expandMonths(from, to) {
  if (!from || !to) return [];
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  if (!fy || !ty) return [];
  const out = [];
  let y = fy, m = fm;
  while ((y < ty || (y === ty && m <= tm)) && out.length < 120) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    if (m === 12) { y++; m = 1; } else m++;
  }
  return out;
}

/** Academic-year start year (the Sep year) from periods. Sep–Dec → that year, Jan–Aug → year-1. */
export function blockWindowStartYear(periods) {
  const allMonths = (periods || []).flatMap((p) => expandMonths(p.from_month, p.to_month));
  if (!allMonths.length) return null;
  const earliest = [...new Set(allMonths)].sort()[0];
  const [ey, em] = earliest.split("-").map(Number);
  return em < 9 ? ey - 1 : ey;
}

/** The academic year a year block covers, written "2026–2027". */
export const academicYearLabel = (periods) => {
  const ws = blockWindowStartYear(periods);
  return ws ? `${ws}–${ws + 1}` : "";
};

/** "Sep 2025 – Dec 2025": a term's months on the read-only panel. */
export function termMonthsLong(from, to) {
  const a = parseYm(from), b = parseYm(to);
  if (!a && !b) return "";
  // A non-breaking space keeps "Sep 2025" whole; a narrow screen wraps at the dash.
  const fmt = (x) => `${SHORT[x.m - 1]}\u00a0${x.y}`;
  if (!a || !b) return fmt(a || b);
  if (a.y === b.y && a.m === b.m) return fmt(a);
  return `${fmt(a)}\u00a0\u2013 ${fmt(b)}`;
}

/** "Sep–Dec 2026" (or "Nov 2026–Feb 2027" across a new year): the copied-term line. */
export function termMonthsShort(from, to) {
  const a = parseYm(from), b = parseYm(to);
  if (!a || !b) return termMonthsLong(from, to);
  if (a.y === b.y && a.m === b.m) return `${SHORT[a.m - 1]} ${a.y}`;
  if (a.y === b.y) return `${SHORT[a.m - 1]}–${SHORT[b.m - 1]} ${a.y}`;
  return `${SHORT[a.m - 1]} ${a.y}–${SHORT[b.m - 1]} ${b.y}`;
}
